import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Chip, makeStyles, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useQuery } from '@tanstack/react-query'
import SydCheckbox from '../../../../../commonDesign/SydCheckbox'
import { useSearchPermissions } from '../../../../../../api/users'
import { fetchUsers } from '../../../../../../service'
import { FEATURE_FLAG } from '../../../../../../constants'

const useStyles = makeStyles(() => ({
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 199
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    boxShadow: 'var(--shadow-md)',
    zIndex: 200,
    padding: '14px',
    display: 'flex',
    flexDirection: 'row',
    gap: 0
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  columnLeft: {
    width: '280px',
    paddingRight: '14px'
  },
  columnRight: {
    width: '180px',
    paddingLeft: '14px',
    borderLeft: '1px solid var(--color-neutral-200)'
  },
  title: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-400)'
  },
  field: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--color-neutral-700)'
  },
  subLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    marginBottom: '3px'
  },
  input: {
    width: '70px',
    padding: '4px 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    textAlign: 'right',
    fontFamily: 'var(--font-mono, monospace)',
    outline: 'none',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  select: {
    width: '100%',
    padding: '4px 8px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  focusRow: {
    display: 'flex',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden',
    border: '1px solid var(--color-neutral-300)'
  },
  focusBtn: {
    flex: 1,
    padding: '4px 0',
    fontSize: '11px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: 'var(--color-neutral-600)',
    transition: 'all 0.1s ease',
    '&:hover': { backgroundColor: 'var(--color-neutral-100)' }
  },
  focusBtnActive: {
    backgroundColor: 'var(--color-primary-500)',
    color: 'white',
    '&:hover': { backgroundColor: 'var(--color-primary-600)' }
  },
  flagRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  flagItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    fontSize: '12px',
    color: 'var(--color-neutral-700)'
  },
  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '3px',
    marginTop: '4px'
  }
}))

const FOCUS_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'none', label: 'None' },
  { value: 'client', label: 'Client' }
]

const FEATURE_OPTIONS = Object.keys(FEATURE_FLAG).sort()

function SkillConfigPopover ({ skillConfig, onConfigChange, models, onClose }) {
  const classes = useStyles()
  const debounceRef = useRef(null)

  const audience = skillConfig?.audience
  const currentFocus = skillConfig?.focuses?.[0] || 'any'

  const [userSearchInput, setUserSearchInput] = useState('')
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('')

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  const handleUserSearchChange = useCallback((val) => {
    setUserSearchInput(val)
    clearTimeout(debounceRef.current)
    if (val.length < 2) { setDebouncedUserSearch(''); return }
    debounceRef.current = setTimeout(() => setDebouncedUserSearch(val), 350)
  }, [])

  const { data: permissionsData } = useSearchPermissions({ skip: 0, take: 500 })
  const { data: usersData } = useQuery({
    queryKey: ['config-user-search', debouncedUserSearch],
    queryFn: async () => {
      const { data } = await fetchUsers({
        searchFields: ['firstName', 'lastName', 'email'],
        searchText: debouncedUserSearch,
        limit: 20,
        offset: 0
      })
      return data
    },
    enabled: debouncedUserSearch.length >= 2
  })

  const permissionOptions = useMemo(() => (permissionsData || []).map(p => p.permissionId || p.name || p), [permissionsData])
  const userOptions = useMemo(() => {
    if (!usersData?.users) return []
    return usersData.users.map(u => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim()
      return { userId: u.internalId, label: name ? `${name} (${u.internalId})` : `User ${u.internalId}` }
    })
  }, [usersData])

  const updateAudience = useCallback((updater) => {
    onConfigChange(prev => {
      const current = prev.audience || {}
      const next = updater(current)
      const isEmpty = !next.isAdmin && !next.isInternal && !next.isSummit &&
        (!next.permissions || next.permissions.length === 0) &&
        (!next.features || next.features.length === 0) &&
        (!next.userIds || next.userIds.length === 0)
      return { ...prev, audience: isEmpty ? undefined : next }
    })
  }, [onConfigChange])

  return (
    <>
      <div className={classes.backdrop} onClick={onClose} />
      <div className={classes.popover}>
        {/* Left column: Audience */}
        <div className={`${classes.column} ${classes.columnLeft}`}>
          <div className={classes.title}>Audience</div>

          <div>
            <div className={classes.subLabel}>User Flags</div>
            <div className={classes.flagRow}>
              <label className={classes.flagItem}>
                <SydCheckbox
                  value={audience?.isAdmin === true}
                  onChange={(e, c) => updateAudience(a => ({ ...a, isAdmin: c || undefined }))}
                  size='small'
                />
                Admin
              </label>
              <label className={classes.flagItem}>
                <SydCheckbox
                  value={audience?.isInternal === true}
                  onChange={(e, c) => updateAudience(a => ({ ...a, isInternal: c || undefined }))}
                  size='small'
                />
                Internal
              </label>
              <label className={classes.flagItem}>
                <SydCheckbox
                  value={audience?.isSummit === true}
                  onChange={(e, c) => updateAudience(a => ({ ...a, isSummit: c || undefined }))}
                  size='small'
                />
                Summit
              </label>
            </div>
          </div>

          <div>
            <div className={classes.subLabel}>Permissions</div>
            <Autocomplete
              size='small'
              options={permissionOptions}
              onChange={(e, val) => {
                if (val && !(audience?.permissions || []).includes(val)) {
                  updateAudience(a => ({ ...a, permissions: [...(a.permissions || []), val] }))
                }
              }}
              renderInput={(params) => (
                <TextField {...params} variant='outlined' size='small' placeholder='Search…' />
              )}
              value={null}
            />
            {audience?.permissions?.length > 0 && (
              <div className={classes.chipWrap}>
                {audience.permissions.map(p => (
                  <Chip
                    key={p} label={p} size='small'
                    onDelete={() => updateAudience(a => ({ ...a, permissions: (a.permissions || []).filter(x => x !== p) }))}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <div className={classes.subLabel}>Features</div>
            <Autocomplete
              size='small'
              options={FEATURE_OPTIONS.filter(f => !(audience?.features || []).includes(f))}
              onChange={(e, val) => {
                if (val) {
                  updateAudience(a => ({ ...a, features: [...(a.features || []), val] }))
                }
              }}
              renderInput={(params) => (
                <TextField {...params} variant='outlined' size='small' placeholder='Search…' />
              )}
              value={null}
            />
            {audience?.features?.length > 0 && (
              <div className={classes.chipWrap}>
                {audience.features.map(f => (
                  <Chip
                    key={f} label={f} size='small'
                    onDelete={() => updateAudience(a => ({ ...a, features: (a.features || []).filter(x => x !== f) }))}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <div className={classes.subLabel}>Specific Users</div>
            <Autocomplete
              size='small'
              options={userOptions}
              getOptionLabel={(opt) => opt.label || ''}
              getOptionSelected={(opt, val) => opt.userId === val.userId}
              inputValue={userSearchInput}
              onInputChange={(e, val, reason) => {
                if (reason === 'reset') return
                handleUserSearchChange(val || '')
              }}
              onChange={(e, val) => {
                if (val?.userId && !(audience?.userIds || []).includes(val.userId)) {
                  updateAudience(a => ({ ...a, userIds: [...(a.userIds || []), val.userId] }))
                }
                handleUserSearchChange('')
              }}
              renderInput={(params) => (
                <TextField {...params} variant='outlined' size='small' placeholder='Min 2 chars…' />
              )}
              value={null}
              filterOptions={(x) => x}
            />
            {audience?.userIds?.length > 0 && (
              <div className={classes.chipWrap}>
                {audience.userIds.map(uid => (
                  <Chip
                    key={uid} label={`User ${uid}`} size='small'
                    onDelete={() => updateAudience(a => ({ ...a, userIds: (a.userIds || []).filter(x => x !== uid) }))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column: Settings */}
        <div className={`${classes.column} ${classes.columnRight}`}>
          <div className={classes.title}>Settings</div>

          <label className={classes.flagItem}>
            <SydCheckbox
              value={skillConfig?.visible !== false}
              onChange={(e, c) => onConfigChange(prev => ({ ...prev, visible: c }))}
              size='small'
            />
            Show in catalog
          </label>

          <div>
            <div className={classes.subLabel}>Focus</div>
            <div className={classes.focusRow}>
              {FOCUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`${classes.focusBtn} ${currentFocus === opt.value ? classes.focusBtnActive : ''}`}
                  onClick={() => onConfigChange(prev => ({ ...prev, focuses: [opt.value] }))}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className={classes.subLabel}>Model</div>
            <select
              className={classes.select}
              value={skillConfig?.modelId || ''}
              onChange={(e) => onConfigChange(prev => ({ ...prev, modelId: e.target.value || null }))}
            >
              <option value=''>Default</option>
              {(models || []).filter(m => m.enabled !== false).map(m => (
                <option key={m.modelId} value={m.modelId}>{m.displayName || m.modelId}</option>
              ))}
            </select>
          </div>

          <div className={classes.field}>
            <span className={classes.label}>Max iterations</span>
            <input
              className={classes.input}
              type='number'
              min={10}
              max={50}
              placeholder='—'
              value={skillConfig?.maxIterations ?? ''}
              onChange={(e) => {
                const raw = e.target.value
                const val = raw === '' ? null : parseInt(raw, 10)
                onConfigChange(prev => ({ ...prev, maxIterations: (val && val >= 10 && val <= 50) ? val : null }))
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}

SkillConfigPopover.propTypes = {
  skillConfig: PropTypes.object.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  models: PropTypes.array,
  onClose: PropTypes.func.isRequired
}

export default SkillConfigPopover
